import React from "react";
import classes from "../homepage/MintSection.module.css";
import { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Button, ButtonBase, Container, Snackbar } from '@material-ui/core';
import * as anchor from '@project-serum/anchor';
import Alert from '@material-ui/lab/Alert';
import { WalletDialogButton } from '@solana/wallet-adapter-material-ui';
import { Header } from './Header';
import { MintButton } from './MintButton';
import Connect from "../web3/Connect";
import { AlertState } from "./utils";
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import {
  awaitTransactionSignatureConfirmation,
  CandyMachineAccount,
  CANDY_MACHINE_PROGRAM,
  getCandyMachineState,
  mintOneToken,
} from './candy-machine';
import { GatewayProvider } from "@civic/solana-gateway-react";
import { PublicKey } from "@solana/web3.js";

export interface MintProps {
  candyMachineId?: anchor.web3.PublicKey;
  connection: anchor.web3.Connection;
  startDate: number;
  txTimeout: number;
  rpcHost: string;
}

const MintContainer = styled.div``; // add your owns styles here

const MintSection = (props: MintProps) => {
  const [isUserMinting, setIsUserMinting] = useState(false);
  const [candyMachine, setCandyMachine] = useState<CandyMachineAccount>();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    message: "",
    severity: undefined,
  });

  const rpcUrl = props.rpcHost;
  const wallet = useWallet();

  const anchorWallet = useMemo(() => {
    if (
      !wallet ||
      !wallet.publicKey ||
      !wallet.signAllTransactions ||
      !wallet.signTransaction
    ) {
      return;
    }

    return {
      publicKey: wallet.publicKey,
      signAllTransactions: wallet.signAllTransactions,
      signTransaction: wallet.signTransaction,
    } as anchor.Wallet;
  }, [wallet]);

  const refreshCandyMachineState = useCallback(async () => {
    if (!anchorWallet) {
      return;
    }

    if (props.candyMachineId) {
      try {
        const cndy = await getCandyMachineState(
          anchorWallet,
          props.candyMachineId,
          props.connection
        );
        setCandyMachine(cndy);
      } catch (e) {
        console.log("There was a problem fetching Candy Machine state");
        console.log(e);
      }
    }
  }, [anchorWallet, props.candyMachineId, props.connection]);

  const onMint = async () => {
    console.log(anchorWallet);
    try {
      setIsUserMinting(true);
      document.getElementById("#identity")?.click();
      if (wallet.connected && candyMachine?.program && wallet.publicKey) {
        const mintTxId = (
          await mintOneToken(candyMachine, wallet.publicKey)
        )[0];

        let status: any = { err: true };
        if (mintTxId) {
          status = await awaitTransactionSignatureConfirmation(
            mintTxId,
            props.txTimeout,
            props.connection,
            true
          );
        }

        if (status && !status.err) {
          setAlertState({
            open: true,
            message: "Congratulations! Mint succeeded!",
            severity: "success",
          });
        } else {
          setAlertState({
            open: true,
            message: "Mint failed! Please try again!",
            severity: "error",
          });
        }
      }
    } catch (error: any) {
      let message = error.msg || "Minting failed! Please try again!";
      if (!error.msg) {
        if (!error.message) {
          message = "Transaction Timeout! Please try again.";
        } else if (error.message.indexOf("0x137")) {
          message = `SOLD OUT!`;
        } else if (error.message.indexOf("0x135")) {
          message = `Insufficient funds to mint. Please fund your wallet.`;
        }
      } else {
        if (error.code === 311) {
          message = `SOLD OUT!`;
          window.location.reload();
        } else if (error.code === 312) {
          message = `Minting period hasn't started yet.`;
        }
      }

      setAlertState({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setIsUserMinting(false);
    }
  };

  useEffect(() => {
    refreshCandyMachineState();
  }, [
    anchorWallet,
    props.candyMachineId,
    props.connection,
    refreshCandyMachineState,
  ]);

  return (
    <div className={classes["mint-wrapper"]}>
      <div className={classes["mint-section-wrapper"]}>
        <div className={classes["mint-title"]}> Start Your Journey</div>
        <div className={classes["mint-text-wrapper"]}>
          <div className={classes["mint-text"]}>
            <div className={classes["mint-text-title"]}>Mint Price</div>
            <div className={classes["mint-text-text"]}>0.25 SOL</div>
          </div>
        </div>
        <div className={classes["mint-text"]}>
          {candyMachine ? (
            <div
              className={`${classes["mint-text-title"]} ${classes["minted"]}`}
            >
              {`${candyMachine?.state.itemsRedeemed}`}/1250 Minted
            </div>
          ) : (
            <div />
          )}
        </div>
        <div className={classes["connect-mint-wrapper"]}>
          {!wallet.connected ? (
            <Connect
              candyMachineId={props.candyMachineId}
              connection={props.connection}
              startDate={props.startDate}
              txTimeout={props.txTimeout}
              rpcHost={props.rpcHost}
            />
          ) : (
            <>
              <MintContainer>
                {candyMachine?.state.isActive &&
                candyMachine?.state.gatekeeper &&
                wallet.publicKey &&
                wallet.signTransaction ? (
                  <GatewayProvider
                    wallet={{
                      publicKey:
                        wallet.publicKey ||
                        new PublicKey(CANDY_MACHINE_PROGRAM),
                      //@ts-ignore
                      signTransaction: wallet.signTransaction,
                    }}
                    gatekeeperNetwork={
                      candyMachine?.state?.gatekeeper?.gatekeeperNetwork
                    }
                    clusterUrl={rpcUrl}
                    options={{ autoShowModal: false }}
                  >
                    <MintButton
                      candyMachine={candyMachine}
                      isMinting={isUserMinting}
                      onMint={onMint}
                    />
                  </GatewayProvider>
                ) : (
                  <MintButton
                    candyMachine={candyMachine}
                    isMinting={isUserMinting}
                    onMint={onMint}
                  />
                )}
              </MintContainer>
            </>
          )}
        </div>
        <div className={classes["mint-text"]}>

          <div className={`${classes["mint-text-title"]} ${classes["minted"]}`}>
            Buy a Space Kitty on &nbsp;
            <a href="https://www.magiceden.io/marketplace/spacekitties">
              MAGIC EDEN
            </a>
            &nbsp;
          </div>
        </div>
      </div>
    </div>
  );
};

export default MintSection;
